import React, { useState, useEffect, useMemo, useCallback } from "react";
import * as API from "../api";
import {
  BG, CARD, WHITE, BLUE, BLUE_MED, BLUE_SOFT, BLUE_DEEP, BORDER,
  TEXT, TEXT2, TEXT3, SUCCESS, WARNING, DANGER,
  SHADOW, SHADOW_SM, SHADOW_LG, FONT, MONO,
  card3d, statBubble, B, Bsm, Bg,
  VellaAvatar, PlatformBadge,
  InstagramLogo, FacebookLogo, TikTokLogo, GoogleAdsLogo,
  StatCard, STAT_GRADIENTS,
} from "../shared";
import { AutopilotContext, MiniBarChart, MiniLineChart } from "../App";
import { SkeletonKPI } from "./ui/Skeleton";

// --- Social Media Engagement Chart (extracted to prevent re-creation on parent render) ---
const ENG_PLATFORMS = [{label:'All Platforms',value:'all',color:'#3B82F6'},{label:'Instagram',value:'instagram',color:'#E1306C'},{label:'Facebook',value:'facebook',color:'#1877F2'},{label:'Google',value:'google',color:'#4285F4'},{label:'TikTok',value:'tiktok',color:'#000000'}];
const ENG_METRICS = [{l:"Likes",key:"likes",color:"#4ECDC4"},{l:"Comments",key:"comments",color:"#3B82F6"},{l:"Shares",key:"shares",color:"#8B5CF6"},{l:"Saves",key:"saves",color:"#F59E0B"}];
const ZERO_ENG = { value: "0", change: "", data: [0,0,0,0,0,0,0,0,0,0,0,0] };
const ZERO_ENG_PLAT = { likes: ZERO_ENG, comments: ZERO_ENG, shares: ZERO_ENG, saves: ZERO_ENG };

function SocialEngagementChart({ liveStats }) {
  const [platform, setPlatform] = React.useState('all');
  const fmtN = (n) => { if (!n && n !== 0) return null; if (n >= 1000000) return (n / 1000000).toFixed(2).replace(/\.?0+$/, "") + "M"; if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.?0+$/, "") + "K"; return n.toLocaleString(); };
  const liveN = (val, demo) => liveStats ? (fmtN(val) || "0") : demo;
  const tag = liveStats ? "Live" : "";
  const zd = [0,0,0,0,0,0,0,0,0,0,0,0];
  const engagementStats = React.useMemo(() => ({
    all: { likes: { value: liveN(liveStats?.totalLikes ?? liveStats?.likes, "0"), change: tag, data: zd }, comments: { value: liveN(liveStats?.totalComments ?? liveStats?.comments, "0"), change: tag, data: zd }, shares: { value: liveN(liveStats?.totalShares ?? liveStats?.shares, "0"), change: tag, data: zd }, saves: { value: liveN(liveStats?.totalSaves ?? liveStats?.saves, "0"), change: tag, data: zd } },
    instagram: ZERO_ENG_PLAT, facebook: ZERO_ENG_PLAT, google: ZERO_ENG_PLAT, tiktok: ZERO_ENG_PLAT,
  }), [liveStats]);
  const cd = engagementStats[platform];
  return (
    <div style={{ ...card3d, padding: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT, margin: 0, letterSpacing: "-0.01em" }}>Social Media Engagement</h3>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {ENG_PLATFORMS.map(p => {
            const isAct = platform === p.value;
            return (
              <button key={p.value} onClick={() => setPlatform(p.value)} style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 14px", borderRadius: 999, border: `1.5px solid ${isAct ? p.color : "rgba(255,255,255,0.65)"}`, background: isAct ? p.color + "1F" : "rgba(255,255,255,0.55)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", color: isAct ? p.color : TEXT2, fontSize: 12, fontWeight: isAct ? 700 : 500, cursor: "pointer", transition: "all 0.2s", boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset" }}>
                {p.value !== "all" && (
                  p.value === "instagram" ? <InstagramLogo size={14} /> :
                  p.value === "facebook" ? <FacebookLogo size={14} /> :
                  p.value === "google" ? <GoogleAdsLogo size={14} /> :
                  <TikTokLogo size={14} />
                )}
                {p.value === "all" && <div style={{ width: 8, height: 8, borderRadius: "50%", background: isAct ? BLUE : TEXT3 }} />}
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {ENG_METRICS.map((s, i) => {
          const d = cd[s.key];
          return (
            <div key={i} style={{ ...statBubble, transition: "all 0.22s cubic-bezier(.4,0,.2,1)", flex: "1 1 140px", minWidth: 140, cursor: "default" }} onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = SHADOW_LG; }} onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = SHADOW; }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: TEXT3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{s.l}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: SUCCESS }}>{d.change}</span>
              </div>
              <p style={{ fontSize: 22, fontWeight: 700, color: TEXT, fontFeatureSettings: "'tnum','cv11'", letterSpacing: "-0.022em", margin: "0 0 10px 0" }}>{d.value}</p>
              <div style={{ height: 36 }}>
                <MiniBarChart data={d.data} color={s.color} height={36} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Live Website + SEO panels --------------------------------------------
// All four cards share the dashboard's glassmorphism vocabulary (card3d /
// statBubble). They render an honest "no data yet" state when the backend
// hasn't connected the relevant integration, matching the rest of the
// dashboard's "never invent stats" rule.
const ZERO_SPARK = [0,0,0,0,0,0,0];

function priorityChip(priority) {
  const p = String(priority || "").toLowerCase();
  if (p === "high") return { bg: "#FEE2E2", fg: "#B91C1C", label: "High" };
  if (p === "medium") return { bg: "#FEF3C7", fg: "#92400E", label: "Medium" };
  return { bg: "#D1FAE5", fg: "#065F46", label: "Low" };
}

function fmtCount(n) {
  if (n === null || n === undefined) return "—";
  if (typeof n === "string") return n;
  if (!Number.isFinite(n)) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return n.toLocaleString();
}

function LiveWebsiteCard({ data }) {
  const status = data?.status;
  const isLive = status === "live" || status === "up" || data?.uptime === true;
  const isDown = status === "down" || data?.uptime === false;
  const dotColor = isLive ? "#10B981" : isDown ? DANGER : TEXT3;
  const dotLabel = isLive ? "Live" : isDown ? "Down" : "Checking…";
  const items = [
    { l: "Page Views", v: data ? fmtCount(data.pageViews) : "—" },
    { l: "Visitors", v: data ? fmtCount(data.visitors) : "—" },
    { l: "Bounce Rate", v: data?.bounceRate != null ? `${Math.round(data.bounceRate)}%` : "—" },
    { l: "Avg Load", v: data?.loadSpeedMs != null ? `${(data.loadSpeedMs / 1000).toFixed(2)}s` : "—" },
  ];
  return (
    <div style={{ ...card3d, padding: 24, flex: "1 1 320px", minWidth: 280, marginBottom: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT, margin: 0, letterSpacing: "-0.01em" }}>Live Website</h3>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: dotColor, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor, boxShadow: isLive ? `0 0 6px ${dotColor}` : "none", animation: isLive ? "pulse 2s infinite" : "none" }} />
          {dotLabel}
        </span>
      </div>
      {data?.url && (
        <p style={{ fontSize: 12, color: TEXT2, margin: "0 0 12px 0", wordBreak: "break-all" }}>{data.url}</p>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        {items.map((it, i) => (
          <div key={i} style={{ ...statBubble, padding: "12px 14px", minWidth: 0 }}>
            <div style={{ fontSize: 10, color: TEXT3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{it.l}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, fontFeatureSettings: "'tnum','cv11'", letterSpacing: "-0.02em" }}>{it.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SeoHealthCard({ data }) {
  const trend = data?.organicTrend;
  const trendColor = trend == null ? TEXT3 : trend >= 0 ? SUCCESS : DANGER;
  const trendStr = trend == null ? "" : `${trend >= 0 ? "+" : ""}${Number(trend).toFixed(1)}%`;
  const items = [
    { l: "Top Keywords", v: data ? fmtCount(data.topKeywords ?? data.rankingKeywords) : "—" },
    { l: "Organic Traffic", v: data ? fmtCount(data.organicTraffic) : "—", sub: trendStr, subColor: trendColor },
    { l: "Indexed Pages", v: data ? fmtCount(data.indexedPages) : "—" },
    { l: "Domain Authority", v: data?.domainAuthority != null ? String(data.domainAuthority) : "—" },
  ];
  return (
    <div style={{ ...card3d, padding: 24, flex: "1 1 320px", minWidth: 280, marginBottom: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT, margin: 0, letterSpacing: "-0.01em" }}>SEO Health</h3>
        <span style={{ fontSize: 11, fontWeight: 700, color: BLUE_DEEP, textTransform: "uppercase", letterSpacing: "0.08em" }}>30d</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        {items.map((it, i) => (
          <div key={i} style={{ ...statBubble, padding: "12px 14px", minWidth: 0 }}>
            <div style={{ fontSize: 10, color: TEXT3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{it.l}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, fontFeatureSettings: "'tnum','cv11'", letterSpacing: "-0.02em" }}>{it.v}</div>
              {it.sub && <div style={{ fontSize: 11, fontWeight: 700, color: it.subColor }}>{it.sub}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AiSeoInsightsCard({ insights }) {
  const list = Array.isArray(insights) ? insights.slice(0, 4) : [];
  return (
    <div style={{ ...card3d, padding: 24, marginBottom: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT, margin: 0, letterSpacing: "-0.01em" }}>AI SEO Insights</h3>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: "#8B5CF6", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l1.9 5.8L20 10l-5.8 1.9L12 18l-1.9-5.8L4 10l5.8-1.9L12 2z"/></svg>
          AI
        </span>
      </div>
      {list.length === 0 ? (
        <p style={{ fontSize: 13, color: TEXT2, lineHeight: 1.6, margin: 0 }}>
          Vella is studying your site. AI recommendations will appear once we have indexed your pages and keyword footprint.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {list.map((ins, i) => {
            const chip = priorityChip(ins.priority);
            return (
              <div key={i} style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)", backdropFilter: "blur(34px) saturate(180%)", WebkitBackdropFilter: "blur(34px) saturate(180%)", border: "1px solid rgba(255,255,255,0.40)", borderRadius: 14, padding: "12px 14px", boxShadow: "0 1px 0 rgba(255,255,255,0.7) inset, 0 8px 22px -10px rgba(11,107,211,0.18)" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: TEXT, margin: 0, lineHeight: 1.4 }}>{ins.title || ins.recommendation || ins.text}</p>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 999, background: chip.bg, color: chip.fg, textTransform: "uppercase", letterSpacing: "0.06em", flexShrink: 0 }}>{chip.label}</span>
                </div>
                {ins.detail && <p style={{ fontSize: 12, color: TEXT2, margin: 0, lineHeight: 1.5 }}>{ins.detail}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WebsiteStatsSparkline({ data }) {
  const points = Array.isArray(data?.traffic7d) && data.traffic7d.length > 0
    ? data.traffic7d
    : ZERO_SPARK;
  const total = points.reduce((a, b) => a + b, 0);
  return (
    <div style={{ ...card3d, padding: 24, flex: "1 1 280px", minWidth: 240, marginBottom: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT, margin: 0, letterSpacing: "-0.01em" }}>Website Stats</h3>
        <span style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: "0.06em" }}>7d</span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: TEXT, fontFeatureSettings: "'tnum','cv11'", letterSpacing: "-0.022em" }}>{total > 0 ? fmtCount(total) : "—"}</span>
        <span style={{ fontSize: 11, color: TEXT3 }}>visits this week</span>
      </div>
      <div style={{ height: 56 }}>
        <MiniLineChart data={points} color="#10B981" height={56} />
      </div>
    </div>
  );
}

function DashboardView({ setActive, biz, setVellaPrompt }) {
  const { autopilot: autopilotActive, toggle: toggleAutopilotCtx } = React.useContext(AutopilotContext);
  const [showBriefing, setShowBriefing] = useState(true);
  const [expandedReasoning, setExpandedReasoning] = useState(null);
  const [dismissed, setDismissed] = useState([]);
  const [approved, setApproved] = useState([]);
  const [declined, setDeclined] = useState([]);
  const [platform, setPlatform] = useState("all");
  const [liveStats, setLiveStats] = useState(null);
  const [insights, setInsights] = useState(null);
  const [briefing, setBriefing] = useState({ posts: 0, campaigns: 0, inbox: 0, scheduled: 0 });
  const [websiteData, setWebsiteData] = useState(null);
  const [seoOverview, setSeoOverview] = useState(null);
  const [seoInsights, setSeoInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load all dashboard data on mount. The cancelled flag prevents
  // setState on an unmounted component if the user navigates away
  // before the parallel fetches resolve — six concurrent promises
  // settling on a torn-down component would otherwise leak state and
  // log dev warnings.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const insightsP = API.getInsights()
      .then(data => { if (!cancelled) setInsights(Array.isArray(data) ? data : data?.insights || []); })
      .catch(() => { if (!cancelled) setInsights([]); });

    const overviewP = API.getAnalyticsOverview("30d").then(data => {
      if (cancelled) return;
      const ov = data.overview || data;
      if (ov && typeof ov === "object") setLiveStats(ov);
    }).catch(() => {});

    const briefingP = Promise.allSettled([
      API.getPostAnalytics({ limit: 100 }),
      API.getCampaignAnalytics({}),
      API.getInbox({ unreplied: true }),
      API.getScheduledPosts(),
    ]).then(([postsR, campsR, inboxR, schedR]) => {
      if (cancelled) return;
      const posts = postsR.status === "fulfilled" ? (postsR.value?.posts?.length || 0) : 0;
      const campaigns = campsR.status === "fulfilled" ? (campsR.value?.campaigns?.filter(c => c.status === "active")?.length || 0) : 0;
      const inbox = inboxR.status === "fulfilled" ? (inboxR.value?.messages?.length || inboxR.value?.inbox?.length || 0) : 0;
      const scheduled = schedR.status === "fulfilled" ? (schedR.value?.posts?.length || 0) : 0;
      setBriefing({ posts, campaigns, inbox, scheduled });
    });

    // Website + SEO panels. All three are best-effort — a connector
    // outage (Wix unlinked, no SEO crawler yet) should render an empty
    // state, never blow up the dashboard. We deliberately don't gate
    // setLoading on these; the existing KPIs render even if the new
    // cards are still empty.
    const websiteP = API.getWebsiteAnalytics()
      .then(data => { if (!cancelled) setWebsiteData(data?.website || data || {}); })
      .catch(() => { if (!cancelled) setWebsiteData({}); });
    const seoP = API.getSeoOverview()
      .then(data => { if (!cancelled) setSeoOverview(data?.seo || data || {}); })
      .catch(() => { if (!cancelled) setSeoOverview({}); });
    const seoInsightsP = API.getSeoInsights()
      .then(data => { if (!cancelled) setSeoInsights(Array.isArray(data) ? data : data?.insights || []); })
      .catch(() => { if (!cancelled) setSeoInsights([]); });

    Promise.allSettled([insightsP, overviewP, briefingP, websiteP, seoP, seoInsightsP]).finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  // Format a raw number into a short readable string (e.g. 1380000 -> "1.38M")
  const fmtN = useCallback((n) => {
    if (!n && n !== 0) return null;
    if (n >= 1000000) return (n / 1000000).toFixed(2).replace(/\.?0+$/, "") + "M";
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.?0+$/, "") + "K";
    return n.toLocaleString();
  }, []);
  const fmtM = useCallback((n) => { const s = fmtN(n); return s ? (s.startsWith("$") ? s : "$" + s) : null; }, [fmtN]);

  // Per-platform KPI data -- memoized so it only recalculates when liveStats changes
  const ZERO_DATA = [0,0,0,0,0,0,0,0,0,0,0,0];
  const ZERO_METRIC = { v: "0", ch: "", data: ZERO_DATA };
  const ZERO_MONEY = { v: "$0", ch: "", data: ZERO_DATA };
  const ZERO_PLATFORM = { revenue: ZERO_MONEY, impressions: ZERO_METRIC, clicks: ZERO_METRIC, conversions: ZERO_METRIC };

  const platformData = useMemo(() => {
    const ls = liveStats;
    const liveM = (val, demo) => ls ? (fmtM(val) || "$0") : demo;
    const liveN2 = (val, demo) => ls ? (fmtN(val) || "0") : demo;
    const tag = ls ? "Live" : "";
    return {
      all: {
        revenue: { v: liveM(ls?.totalRevenue ?? ls?.revenue ?? ls?.totalSpend ?? ls?.spend, "$0"), ch: tag, data: ZERO_DATA },
        impressions: { v: liveN2(ls?.totalImpressions ?? ls?.impressions, "0"), ch: tag, data: ZERO_DATA },
        clicks: { v: liveN2(ls?.totalClicks ?? ls?.clicks, "0"), ch: tag, data: ZERO_DATA },
        conversions: { v: liveN2(ls?.totalConversions ?? ls?.conversions, "0"), ch: tag, data: ZERO_DATA },
      },
      instagram: ZERO_PLATFORM,
      facebook: ZERO_PLATFORM,
      google: ZERO_PLATFORM,
      tiktok: ZERO_PLATFORM,
    };
  }, [liveStats, fmtN, fmtM]);

  const platforms = [
    { id: "all", label: "All Platforms", color: BLUE },
    { id: "instagram", label: "Instagram", color: "#E1306C" },
    { id: "facebook", label: "Facebook", color: "#1877F2" },
    { id: "google", label: "Google", color: "#4285F4" },
    { id: "tiktok", label: "TikTok", color: "#000000" },
  ];

  const pd = platformData[platform];
  const activePlatform = platforms.find(p => p.id === platform);

  // Recommendations are derived from real briefing counts. We refuse to
  // invent specific stats (reach %, ROAS, etc.) — surfacing fake numbers
  // to a paying customer with no real data destroys trust. Each task is
  // shown only when the underlying signal exists; otherwise we render an
  // honest empty state below.
  const todayTasks = [
    briefing.inbox > 0 && {
      id: "inbox",
      platform: "facebook",
      title: `${briefing.inbox} unanswered ${briefing.inbox === 1 ? "message" : "messages"} in your inbox`,
      desc: "Reply now to keep response time short — fast replies convert better.",
      cta: "Open Inbox",
      tab: "inbox",
      priority: "high",
      type: "reply",
    },
    briefing.scheduled === 0 && briefing.posts === 0 && {
      id: "no-content",
      platform: "instagram",
      title: "Nothing scheduled yet",
      desc: "Ask Vella to draft a week of content so your feed stays active.",
      cta: "Create Posts",
      tab: "talkToVella",
      priority: "medium",
      type: "post",
    },
    briefing.campaigns === 0 && {
      id: "no-campaigns",
      platform: "facebook",
      title: "No active ad campaigns",
      desc: "Launch a campaign in Vella Relay when you're ready to drive traffic.",
      cta: "Build Campaign",
      tab: "talkToVella",
      priority: "low",
      type: "budget",
    },
  ].filter(Boolean).filter(t => !dismissed.includes(t.id));

  const pColors = { high: DANGER, medium: WARNING, low: SUCCESS };

  const PlatLogo = ({ p, size = 20 }) => {
    if (p === "instagram") return <InstagramLogo size={size} />;
    if (p === "facebook") return <FacebookLogo size={size} />;
    if (p === "tiktok") return <TikTokLogo size={size} />;
    if (p === "google") return <GoogleAdsLogo size={size} />;
    return null;
  };

  const hoverCard = {
    ...card3d, marginBottom: 10, cursor: "default",
    transition: "all 0.22s cubic-bezier(0.34,1.56,0.64,1)"
  };


  if (loading) {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 24 }}>
          <VellaAvatar size={48} />
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: TEXT, marginBottom: 2, letterSpacing: "-0.022em" }}>
              {new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 17 ? "Good afternoon" : "Good evening"}{biz?.name ? `, ${biz.name}` : ""}
            </h2>
            <p style={{ color: TEXT2, fontSize: 14 }}>Pulling your latest numbers…</p>
          </div>
        </div>
        <SkeletonKPI tiles={4} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 24 }}>
        <VellaAvatar size={48} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: TEXT, marginBottom: 2, letterSpacing: "-0.022em" }}>
            {new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 17 ? "Good afternoon" : "Good evening"}{biz?.name ? `, ${biz.name}` : ""}
           <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "#10B981", fontWeight: 600, marginLeft: 8 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#10B981", display: "inline-block", animation: "pulse 2s infinite" }} />Live</span></h2>
          <p style={{ color: TEXT2, fontSize: 14 }}>I've been working autonomously on your marketing. Here's what needs your attention.</p>
        </div>
        <button
          type="button"
          onClick={() => API.openMonthlyReport({ days: 30, autoPrint: true })}
          title="Open the last 30 days as a printable PDF report"
          style={{
            ...Bg,
            borderRadius: 999,
            padding: "9px 16px",
            fontSize: 12,
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            color: TEXT,
            flexShrink: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
          </svg>
          Download Monthly Report
        </button>
      </div>

      {/* Autopilot Toggle — liquid glass */}
              <div style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)", backdropFilter: "blur(34px) saturate(180%)", WebkitBackdropFilter: "blur(34px) saturate(180%)", borderRadius: 20, padding: "14px 20px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid rgba(255,255,255,0.40)", boxShadow: autopilotActive ? "0 1px 0 rgba(255,255,255,0.7) inset, 0 0 0 1px rgba(16,185,129,0.25), 0 16px 40px -18px rgba(16,185,129,0.35)" : "0 1px 0 rgba(255,255,255,0.7) inset, 0 10px 28px -12px rgba(11,107,211,0.18)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {autopilotActive && <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#10B981", boxShadow: "0 0 6px #10B981", animation: "pulse 2s infinite" }} />}
                  <span style={{ fontWeight: 600, fontSize: 14, color: TEXT }}>Agent Autopilot</span>
                  <span style={{ fontSize: 12, color: TEXT2 }}>{autopilotActive ? "ON \u2014 Vella is working" : "OFF \u2014 Manual mode"}</span>
                </div>
                <div onClick={() => toggleAutopilotCtx()} style={{ width: 44, height: 24, borderRadius: 12, background: autopilotActive ? "linear-gradient(135deg, #34D399, #10B981)" : "rgba(180,205,235,0.55)", cursor: "pointer", position: "relative", transition: "background 0.2s", boxShadow: autopilotActive ? "0 4px 10px rgba(16,185,129,0.30), inset 0 1px 0 rgba(255,255,255,0.3)" : "inset 0 1px 2px rgba(11,107,211,0.15)" }}>
                  <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: autopilotActive ? 22 : 2, transition: "left 0.2s", boxShadow: "0 2px 6px rgba(11,107,211,0.25)" }} />
                </div>
              </div>
              {/* Today's Briefing — liquid glass */}
              {showBriefing && <div style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)", backdropFilter: "blur(34px) saturate(180%)", WebkitBackdropFilter: "blur(34px) saturate(180%)", borderRadius: 22, padding: 20, marginBottom: 20, border: "1px solid rgba(255,255,255,0.40)", boxShadow: "0 1px 0 rgba(255,255,255,0.7) inset, 0 18px 44px -18px rgba(11,107,211,0.18), 0 8px 24px -10px rgba(14,165,233,0.12)", position: "relative" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, margin: 0 }}>Today's Briefing</h3>
                  <span onClick={() => setShowBriefing(false)} style={{ cursor: "pointer", color: TEXT3, fontSize: 18, lineHeight: 1 }}>&times;</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
                  {[
                    { label: "Posts Published", value: String(briefing.posts), grad: STAT_GRADIENTS.green, tab: "create-posts" },
                    { label: "Campaigns Active", value: String(briefing.campaigns), grad: STAT_GRADIENTS.blue, tab: "ad-campaigns" },
                    { label: "Inbox Unreplied", value: String(briefing.inbox), grad: STAT_GRADIENTS.orange, tab: "inbox" },
                    { label: "Scheduled Posts", value: String(briefing.scheduled), grad: STAT_GRADIENTS.purple, tab: "create-posts" }
                  ].map((item, i) => (
                    <div
                      key={i}
                      onClick={() => item.tab && setActive(item.tab)}
                      style={{
                        background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)",
                        backdropFilter: "blur(34px) saturate(180%)",
                        WebkitBackdropFilter: "blur(34px) saturate(180%)",
                        border: "1px solid rgba(255,255,255,0.40)",
                        borderRadius: 18,
                        padding: "14px 16px",
                        cursor: item.tab ? "pointer" : "default",
                        transition: "all 0.2s",
                        boxShadow: "0 1px 0 rgba(255,255,255,0.7) inset, 0 8px 22px -10px rgba(11,107,211,0.18)",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                      onMouseEnter={e => { if (item.tab) { e.currentTarget.style.background = "linear-gradient(135deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.28) 100%)"; e.currentTarget.style.transform = "translateY(-2px)"; } }}
                      onMouseLeave={e => { e.currentTarget.style.background = "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)"; e.currentTarget.style.transform = "none"; }}
                    >
                      <div style={{ width: 34, height: 34, borderRadius: 10, background: item.grad, flexShrink: 0, boxShadow: "0 6px 14px -6px rgba(11,107,211,0.25), inset 0 1px 0 rgba(255,255,255,0.35)" }} />
                      <div>
                        <div style={{ fontSize: 12, color: TEXT3, fontWeight: 500, letterSpacing: "-0.005em", marginBottom: 2 }}>{item.label}</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: TEXT, letterSpacing: "-0.022em", fontFeatureSettings: "'tnum','cv11'" }}>{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>}
              {/* Platform Filter Dropdown + KPIs */}
      <div style={{ marginBottom: 20 }}>
        {/* Dropdown row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>Performance Overview</h3>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {platforms.map(p => {
              const isActive = platform === p.id;
              return (
                <button key={p.id} onClick={() => setPlatform(p.id)}
                  style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 14px", borderRadius: 999, border: `1.5px solid ${isActive ? p.color : "rgba(255,255,255,0.65)"}`, background: isActive ? p.color + "1F" : "rgba(255,255,255,0.55)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", color: isActive ? p.color : TEXT2, fontSize: 12, fontWeight: isActive ? 700 : 500, cursor: "pointer", fontFamily: FONT, transition: "all 0.2s", boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset" }}>
                  {p.id !== "all" && (
                    p.id === "instagram" ? <InstagramLogo size={14} /> :
                    p.id === "facebook" ? <FacebookLogo size={14} /> :
                    p.id === "google" ? <GoogleAdsLogo size={14} /> :
                    <TikTokLogo size={14} />
                  )}
                  {p.id === "all" && <div style={{ width: 8, height: 8, borderRadius: "50%", background: isActive ? BLUE : TEXT3 }} />}
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* KPI Cards — image-4 style: gradient icon badge + change chip + big number + mini bar chart */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
          {[
            { l: "Revenue (MTD)", key: "revenue", grad: STAT_GRADIENTS.green, chartColor: "#10B981", icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
              ) },
            { l: "Impressions", key: "impressions", grad: STAT_GRADIENTS.blue, chartColor: "#2563EB", icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              ) },
            { l: "Total Clicks", key: "clicks", grad: STAT_GRADIENTS.purple, chartColor: "#9333EA", icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11.24V7.5a2.5 2.5 0 0 1 5 0v3.74M7 11.24h10a2 2 0 0 1 2 2v1.76a6 6 0 0 1-6 6H11a6 6 0 0 1-6-6v-1.76a2 2 0 0 1 2-2z"/></svg>
              ) },
            { l: "Conversions", key: "conversions", grad: STAT_GRADIENTS.orange, chartColor: "#EA580C", icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              ) },
          ].map((s, i) => {
            const d = pd[s.key];
            const changeStr = typeof d.ch === "string" ? d.ch : String(d.ch || "");
            const positive = !changeStr.startsWith("-");
            const formattedChange = changeStr && !/^[+-]/.test(changeStr) ? `+${changeStr}` : changeStr;
            return (
              <StatCard
                key={i}
                gradient={s.grad}
                chartColor={s.chartColor}
                icon={s.icon}
                label={s.l}
                value={d.v}
                change={formattedChange}
                changePositive={positive}
                chartData={Array.isArray(d.data) ? d.data : []}
                style={{ cursor: "default" }}
              />
            );
          })}
        </div>
      </div>

        <SocialEngagementChart liveStats={liveStats} />

      {/* Website + SEO panels — sits between social engagement and
          "What Vella Learned". Top row pairs Live Website with SEO
          Health; bottom row pairs the AI SEO recommendations with the
          7-day traffic sparkline. Wraps to a single column under ~720px
          via the flex `1 1` min-widths on each card. */}
      <div style={{ marginTop: 14 }}>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
          <LiveWebsiteCard data={websiteData} />
          <SeoHealthCard data={seoOverview} />
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <div style={{ flex: "2 1 420px", minWidth: 300, display: "flex" }}>
            <div style={{ flex: 1 }}>
              <AiSeoInsightsCard insights={seoInsights} />
            </div>
          </div>
          <WebsiteStatsSparkline data={websiteData} />
        </div>
      </div>

      {/* What Vella Learned */}
      <div style={{ ...card3d, padding: 28, marginTop: 14 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT, margin: "0 0 16px 0", letterSpacing: "-0.01em" }}>What Vella Learned</h3>
        {insights === null ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: TEXT3, fontSize: 13 }}>
            <div style={{ width: 14, height: 14, border: `2px solid ${BORDER}`, borderTopColor: BLUE, borderRadius: "50%", animation: "spin .7s linear infinite" }} />
            Loading insights...
          </div>
        ) : insights.length === 0 ? (
          <p style={{ fontSize: 13, color: TEXT2, lineHeight: 1.6, margin: 0 }}>
            Vella is learning from your data. Insights will appear after your first week of posting.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {insights.slice(0, 5).map((insight, i) => {
              const categoryColors = { content: "#4ECDC4", timing: "#3B82F6", audience: "#8B5CF6", ads: "#F59E0B" };
              const cat = (insight.category || "content").toLowerCase();
              const catColor = categoryColors[cat] || "#4ECDC4";
              const confidence = insight.confidence ?? insight.score ?? 0;
              const confColor = confidence >= 80 ? SUCCESS : confidence >= 60 ? WARNING : DANGER;
              return (
                <div key={i} style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)", backdropFilter: "blur(34px) saturate(180%)", WebkitBackdropFilter: "blur(34px) saturate(180%)", border: "1px solid rgba(255,255,255,0.40)", borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12, boxShadow: "0 1px 0 rgba(255,255,255,0.7) inset, 0 8px 22px -10px rgba(11,107,211,0.18)" }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 13, color: TEXT, lineHeight: 1.5, margin: "0 0 8px 0", fontWeight: 500 }}>{insight.insight || insight.text}</p>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: catColor + "18", color: catColor, textTransform: "capitalize" }}>{cat}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: confColor + "18", color: confColor }}>{confidence}% confidence</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Financial Intelligence */}
      <div style={{ marginTop: 14, marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 14 }}>Financial Intelligence</h3>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {(() => {
            const rev = liveStats?.totalRevenue ?? liveStats?.revenue ?? liveStats?.totalSpend ?? 0;
            const spend = liveStats?.totalSpend ?? liveStats?.spend ?? 0;
            const clicks = liveStats?.totalClicks ?? liveStats?.clicks ?? 0;
            const conv = liveStats?.totalConversions ?? liveStats?.conversions ?? 0;
            const roas = spend > 0 ? (rev / spend).toFixed(1) : "0";
            const cpc = clicks > 0 ? (spend / clicks).toFixed(2) : "0";
            const forecast = rev > 0 ? "$" + fmtN(Math.round(rev * 1.15)) : "$0";
            const roi = spend > 0 ? Math.round(rev / Math.max(spend, 1)) + "x" : "--";
            return [
              { label: "Revenue (30d)", sub: "Total tracked revenue", value: rev > 0 ? "$" + fmtN(rev) : "$0", badge: rev > 0 ? "Live" : "", badgeUp: true, color: "#4ECDC4" },
              { label: "Ad Spend", sub: "Total ad spend this period", value: spend > 0 ? "$" + fmtN(spend) : "$0", detail: clicks > 0 ? "$" + cpc + " per click" : "", color: "#3B82F6" },
              { label: "ROAS", sub: "Return on ad spend", value: roas + "x", detail: conv > 0 ? conv + " conversions" : "No conversions yet", color: "#8B5CF6" },
              { label: "Forecast", sub: "Next month estimate", value: forecast, badge: rev > 0 ? "+15%" : "", badgeUp: true, detail: "based on current trend", color: WARNING },
            ];
          })().map((card, i) => (
            <div key={i} style={{ ...statBubble, flex: "1 1 200px", transition: "all 0.22s cubic-bezier(.4,0,.2,1)", cursor: "default" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = SHADOW_LG; e.currentTarget.style.borderColor = card.color + "55"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = SHADOW_SM; e.currentTarget.style.borderColor = "rgba(255,255,255,0.45)"; }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: card.color, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: "0.04em" }}>{card.label}</span>
              </div>
              <p style={{ fontSize: 11, color: TEXT2, marginBottom: 10 }}>{card.sub}</p>
              {card.body ? (
                <p style={{ fontSize: 13, fontWeight: 600, color: TEXT, lineHeight: 1.5 }}>{card.body}</p>
              ) : (
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 22, fontWeight: 700, color: TEXT, fontFamily: "inherit", fontFeatureSettings: "'tnum','cv11'", letterSpacing: "-0.022em" }}>{card.value}</span>
                  {card.badge && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: card.badgeUp ? SUCCESS : DANGER, background: card.badgeUp ? `${SUCCESS}18` : `${DANGER}18`, padding: "2px 7px", borderRadius: 6 }}>
                      {card.badgeUp ? "+" : ""}{card.badge}
                    </span>
                  )}
                  {card.detail && <span style={{ fontSize: 12, color: TEXT2 }}>{card.detail}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tasks with approval buttons */}
      {todayTasks.length > 0 && <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 12 }}>Vella's Priorities for Today</h3>
        {todayTasks.map((task) => {
          const isApproved = approved.includes(task.id);
          const isDeclined = declined.includes(task.id);
          return (
            <div key={task.id}
              style={{ position: "relative", ...hoverCard, padding: 20, borderLeft: `3px solid ${pColors[task.priority]}`, opacity: isDeclined ? 0.5 : 1 }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px) scale(1.01)"; e.currentTarget.style.boxShadow = SHADOW_LG; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = SHADOW; }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <div style={{ flexShrink: 0, marginTop: 2 }}><PlatLogo p={task.platform} size={24} /></div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 4 }}>{task.title}</p>
                  <p style={{ fontSize: 13, color: TEXT2, lineHeight: 1.5, marginBottom: 8 }}>{task.desc}</p>
                  {/* Benefit callout — only when we actually have a measured benefit */}
                  {task.benefit && (
                    <div style={{ background: `${SUCCESS}0F`, border: `1px solid ${SUCCESS}22`, borderRadius: 8, padding: "7px 12px", marginBottom: 12, fontSize: 12, color: SUCCESS, fontWeight: 500 }}>
                      Benefit: {task.benefit}
                    </div>
                  )}
                  {task.confidence && <div style={{ position: "absolute", top: 12, right: 12 }}>
                    <span style={{
                      background: task.confidence >= 90 ? "#D1FAE5" : "#FEF3C7",
                      color: task.confidence >= 90 ? "#065F46" : "#92400E",
                      fontSize: 12, fontWeight: 700, padding: "3px 10px",
                      borderRadius: 20
                    }}>{task.confidence}% confident</span>
                  </div>}
                  {task.reasoning && <div style={{ marginTop: 8 }}>
                    <span onClick={() => setExpandedReasoning(expandedReasoning === task.id ? null : task.id)}
                      style={{ color: "#4ECDC4", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                      {expandedReasoning === task.id ? "Hide reasoning \u25B2" : "Why Vella recommends this \u25BC"}
                    </span>
                    {expandedReasoning === task.id && <div style={{
                      marginTop: 6, padding: "8px 12px", background: "#F0FDFA",
                      borderRadius: 8, fontSize: 12, color: "#1A1A2E", lineHeight: 1.5,
                      borderLeft: "3px solid #4ECDC4"
                    }}>{task.reasoning}</div>}
                  </div>}
                  {isApproved ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: SUCCESS, background: `${SUCCESS}18`, padding: "6px 14px", borderRadius: 8 }}>Approved -- Vella is on it</span>
                      <button onClick={() => setActive(task.tab)} style={{ ...Bsm }}>Go to {task.cta}</button>
                    </div>
                  ) : isDeclined ? (
                    <span style={{ fontSize: 12, color: TEXT3 }}>Skipped</span>
                  ) : autopilotActive ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 14px", background: "#ECFDF5", borderRadius: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                      <span style={{ fontSize: 12, color: "#10B981", fontWeight: 600 }}>Vella handled this</span>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button onClick={() => setApproved(p => [...p, task.id])} style={{ ...B, padding: "8px 18px", fontSize: 13, borderRadius: 999, background: SUCCESS }}>Approve</button>
                      <button onClick={() => setActive(task.tab)} style={{ ...Bsm }}>{task.cta}</button>
                      <button onClick={() => { setDismissed(p => [...p, task.id]); setDeclined(p => [...p, task.id]); }} style={{ ...Bg, padding: "8px 14px", fontSize: 12, borderRadius: 999 }}>Dismiss</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>}

      {/* Quick Actions with hover */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { label: "Create Posts", desc: "AI social content", tab: "create-posts", plats: ["Instagram", "Facebook", "TikTok"] },
          { label: "Build Campaign", desc: "Ads in minutes", tab: "ad-campaigns", plats: ["Facebook Ads", "Instagram Ads", "TikTok Ads", "Google Ads"] , confidence: 96, reasoning: "Campaign ROAS is 4.2x vs industry avg 2.1x. Reallocating $50/day from underperforming ad sets could increase conversions by 35%."},
          { label: "Inbox", desc: "Reply to messages", tab: "inbox", plats: ["Instagram", "Facebook"] },
          { label: "My Website", desc: "SEO + AI Search", tab: "website", plats: [] },
        ].map((item, i) => (
          <div key={i} onClick={() => setActive(item.tab)}
            style={{ ...card3d, flex: 1, minWidth: 200, cursor: "pointer", marginBottom: 0, transition: "all 0.22s cubic-bezier(0.34,1.56,0.64,1)", position: "relative" }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px) scale(1.02)"; e.currentTarget.style.boxShadow = SHADOW_LG; e.currentTarget.style.borderColor = BLUE_MED; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = SHADOW; e.currentTarget.style.borderColor = "rgba(255,255,255,0.45)"; }}>
            {item.badge && (
              <span style={{ position: "absolute", top: 10, right: 10, fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "#fff", background: "linear-gradient(135deg,#0EA5E9,#6366F1)", padding: "2px 6px", borderRadius: 999 }}>{item.badge}</span>
            )}
            <p style={{ fontWeight: 700, fontSize: 15, color: TEXT, marginBottom: 2 }}>{item.label}</p>
            <p style={{ color: TEXT2, fontSize: 13, marginBottom: 10 }}>{item.desc}</p>
            <div style={{ display: "flex", gap: 6 }}>{item.plats.map(p => <PlatformBadge key={p} platform={p} size={20} />)}</div>
          </div>
        ))}
      </div>

      {/* Intelligence Tools */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 14, letterSpacing: "-0.01em" }}>Intelligence Tools</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
          {[
            {
              label: "Trending Topics",
              desc: "Discover what's hot in your niche right now",
              color: "#4ECDC4",
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ECDC4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
              ),
              onClick: () => {
                if (setVellaPrompt) setVellaPrompt("What are the top trending topics in my industry right now? Give me content ideas I can post today based on current trends.");
                setActive("talkToVella");
              },
            },
            {
              label: "Content Calendar",
              desc: "AI-generated posting schedule for the week",
              color: "#3B82F6",
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                  <line x1="10" y1="14" x2="14" y2="14" />
                  <line x1="10" y1="18" x2="14" y2="18" />
                </svg>
              ),
              onClick: () => {
                if (setVellaPrompt) setVellaPrompt("Create a content calendar for me for the next 7 days. Include optimal posting times, platforms, and content themes for each day.");
                setActive("talkToVella");
              },
            },
            {
              label: "Score Leads",
              desc: "AI-ranked leads from your inbox messages",
              color: "#8B5CF6",
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87" />
                  <path d="M16 3.13a4 4 0 010 7.75" />
                </svg>
              ),
              onClick: () => setActive("inbox"),
            },
            {
              label: "Budget Optimizer",
              desc: "Maximize ROAS with AI spend allocation",
              color: "#F59E0B",
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                </svg>
              ),
              onClick: () => setActive("performance"),
            },
            {
              label: "Competitor Monitor",
              desc: "Analyze what competitors are doing",
              color: "#EF4444",
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                  <line x1="11" y1="8" x2="11" y2="14" />
                </svg>
              ),
              onClick: () => {
                if (setVellaPrompt) setVellaPrompt("Run a competitor analysis for my business. What are my top competitors doing on social media? What strategies are working for them that I should try?");
                setActive("talkToVella");
              },
            },
          ].map((tool, i) => (
            <div
              key={i}
              onClick={tool.onClick}
              style={{
                ...card3d,
                padding: "20px 18px",
                cursor: "pointer",
                marginBottom: 0,
                borderLeft: `3px solid ${tool.color}`,
                transition: "all 0.22s cubic-bezier(0.34,1.56,0.64,1)",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "translateY(-4px) scale(1.02)";
                e.currentTarget.style.boxShadow = SHADOW_LG;
                e.currentTarget.style.borderColor = tool.color + "55";
                e.currentTarget.style.borderLeftColor = tool.color;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow = SHADOW;
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.45)";
                e.currentTarget.style.borderLeftColor = tool.color;
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: tool.color + "15",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  {tool.icon}
                </div>
                <span style={{ fontWeight: 700, fontSize: 14, color: TEXT }}>{tool.label}</span>
              </div>
              <p style={{ fontSize: 12, color: TEXT2, lineHeight: 1.5, margin: 0 }}>{tool.desc}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

export default DashboardView;
